import React, { useEffect, useMemo } from 'react'
import Box from '../../components/box'
import Typography from '../../components/typography'
import Card from '../../components/card'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField, { SelectOption } from '../../components/textfield';
import { useOnInputChange } from '../../hooks/useOnInputChange';
import Button from '../../components/button';
import { Time } from '../../utils/time';
import { useAsyncOperation } from '../../hooks/useAsyncOperation';
import { StudioService } from '../../apilibrary';
import { RequestStudioBookingInput } from '../../apilibrary/types/studio';
import useCurrentUserId from '../../hooks/useCurrentUserId';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { IDateRange, IDayJSRange } from '../../apilibrary/types/common';
import { Divider } from '@mui/material';
import { trackBookingInteraction } from '../../utils/analytics';

interface BookingProps {
  studioId: string
  hourlyRate: number
  minSessionLength: number
}

const Booking: React.FC<BookingProps> = ({ hourlyRate, studioId, minSessionLength }) => {
  const [availableTimeRanges, setAvailableTimeRanges] = React.useState<IDayJSRange[]>([])
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>()
  const [selectedStartDateISOString, onChangeSelectStartDate] = useOnInputChange('')
  const [selectedEndDateISOString, onChangeSelectedEndDate] = useOnInputChange('')
  const navigate = useNavigate()
  const currentUserId = useCurrentUserId()
  const {
    runFunction: requestBooking,
    loading: isRequestingBooking,
    errorMessage: requestBookingError
  } = useAsyncOperation(StudioService.requestStudioBooking)
  const [generalErrorMessage, setGeneralErrorMessage] = React.useState<string>()

  useEffect(() => {
    const getOpenTimes = async (): Promise<void> => {
      if (!selectedDate) return
      const timeRanges = await StudioService.getStudioOpenAvailabilities(studioId, selectedDate.toDate())
      const dayjsTimeRanges = timeRanges.map((timeRange: IDateRange) => {
        return {
          startDate: dayjs(timeRange.startDate),
          endDate: dayjs(timeRange.endDate)
        }
      })
      setAvailableTimeRanges(dayjsTimeRanges)
    }
    void getOpenTimes()
  }, [selectedDate, studioId])

  const handleRequestBooking = async (): Promise<void> => {
    if (!currentUserId) {
      navigate('/auth/login?redirect=' + window.location.pathname)
    }
    try {
      setGeneralErrorMessage(undefined)
      if (!selectedDate) throw new Error('No date selected')
      if (!selectedStartDateISOString) throw new Error('No start time selected')
      if (!selectedEndDateISOString) throw new Error('No end time selected')
    } catch (e) {
      if (e instanceof Error) {
        setGeneralErrorMessage(e.message)
      }
      return
    }
    const startTime = new Date(selectedStartDateISOString)
    const endTime = new Date(selectedEndDateISOString)
    if (startTime.getTime() >= endTime.getTime()) throw new Error('Start time must be before end time')
    const input: RequestStudioBookingInput = {
      studioId,
      requesterUserId: currentUserId as string,
      startTime,
      endTime
    }
    await requestBooking(input)
    await trackBookingInteraction(
      startTime.toISOString(),
      endTime.toISOString(),
      selectedStartDateISOString,
      selectedEndDateISOString,
      studioId,
      currentUserId as string
    )
    navigate('/bookings')
  }

  const availableStartTimes: Dayjs[] = useMemo(() => {
    if (availableTimeRanges.length === 0) return []
    const result: Dayjs[] = []
    availableTimeRanges.forEach((dates) => {
      const maxStartTime = dates.endDate.subtract(minSessionLength, 'minute')
      result.push(
        ...Time.getDatesBetween(dates.startDate, maxStartTime, 15)
      )
    })
    return result
  }, [availableTimeRanges, minSessionLength])

  const availableEndTimes: Dayjs[] = useMemo(() => {
    if (selectedStartDateISOString === '') return []
    const selectedStartTime = dayjs(selectedStartDateISOString)
    const availabileTimeRangeIndex = availableTimeRanges.findIndex((timeRange) => {
      if (selectedStartTime.isSame(timeRange.startDate)) return true
      if (selectedStartTime.isAfter(timeRange.startDate) && selectedStartTime.isBefore(timeRange.endDate)) return true
      return false
    })
    if (availabileTimeRangeIndex === -1) return []
    const availableEndTimeRange = availableTimeRanges[availabileTimeRangeIndex]
    const minEndTime = selectedStartTime.add(minSessionLength, 'minute')
    const maxEndTime = availableEndTimeRange.endDate
    const result = Time.getDatesBetween(minEndTime, maxEndTime, 15)
    return result
  }, [availableTimeRanges, minSessionLength, selectedStartDateISOString])

  const readableLocalTime = (time: Dayjs): string => {
    const readableHour = time.hour() % 12 || 12
    const readableMinute = time.minute().toString().padStart(2, '0')
    const amPm = time.hour() < 12 ? 'AM' : 'PM'
    return `${readableHour}:${readableMinute} ${amPm}`
  }

  const duration = useMemo(() => {
    if (!selectedStartDateISOString || !selectedEndDateISOString) return undefined
    const startTime = new Date(selectedStartDateISOString)
    const endTime = new Date(selectedEndDateISOString)
    const duration = (endTime.getTime() - startTime.getTime()) / 1000 / 60 / 60
    return duration
  }, [selectedStartDateISOString, selectedEndDateISOString])

  const totalCost = useMemo(() => {
    if (!duration) return undefined
    return duration * hourlyRate
  }, [duration, hourlyRate])

  return (
    <Card raised sx={{ paddingBottom: 2.5, borderRadius: '8px' }}>
      <Box padding={2}>
        <Box display={'flex'} flexDirection={'column'} gap={1.5}>
          <Box>
            <Typography display='inline-block' fontWeight={'bold'} fontSize={'24px'}>${hourlyRate}</Typography>
            <Typography display="inline-block" fontSize={'16px'}>&nbsp; / hour</Typography>
          </Box>
          <DatePicker
            minDate={dayjs()}
            maxDate={dayjs().add(3, 'month')}
            value={selectedDate}
            onChange={setSelectedDate}
            sx={{ width: '100%' }}
            label="Booking Date"
          />
          <TextField
            value={selectedStartDateISOString}
            onChange={onChangeSelectStartDate}
            fullWidth
            select
            disabled={availableStartTimes.length === 0}
            variant='outlined'
            label="Start Time"
          >
            {availableStartTimes.map((time) => (
              <SelectOption key={time.toISOString()} value={time.toISOString()}>
                {readableLocalTime(time)}
              </SelectOption>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            disabled={availableEndTimes.length === 0}
            value={selectedEndDateISOString}
            onChange={onChangeSelectedEndDate}
            variant='outlined'
            label="End Time"
          >
            {availableEndTimes.map((time) => (
              <SelectOption key={time.toISOString()} value={time.toISOString()}>
                {readableLocalTime(time)}
              </SelectOption>
            ))}
          </TextField>
          {requestBookingError && <Typography color={'error'}>{requestBookingError}</Typography>}
          {generalErrorMessage && <Typography color={'error'}>{generalErrorMessage}</Typography>}
          {totalCost && (
            <Box mt={2}>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Typography fontSize={'16px'} fontWeight={'light'}>${hourlyRate} x {duration} hours</Typography>
                <Typography fontSize={'16px'} fontWeight={'light'}>${totalCost}</Typography>
              </Box>
              <Divider sx={{ mb: 2, mt: 2 }} />
              <Box display={'flex'} justifyContent={'space-between'}>
                <Typography fontWeight={'bold'}>Total Cost</Typography>
                <Typography fontWeight={'bold'}>${totalCost}</Typography>
              </Box>
            </Box>
          )}
          <Button
            disabled={isRequestingBooking}
            sx={{ mt: 1 }}
            fullWidth
            variant='contained'
            onClick={handleRequestBooking}
          >
            Request Booking
          </Button>
        </Box>
      </Box>
    </Card>
  )
}

export default Booking
